import { DossierDetailsContent, DossierDetailsHeader, DossierDetailsPanel } from "@/app/pages/dossier/dossier-details-panel.tsx";
import { AangevraagdeVestiging, AangevraagdeVestigingOpeningstijd, Vestiging } from "@/entities/vestiging";
import { ActionsDropdown, Button, DropdownMenuItem } from "@/shared/components";
import { useCallback, useMemo, useState } from "react";
import { VestigingenUpdateHandler } from "../../shared";
import { FaciliteitenDetailsForStation } from "./faciliteiten-details-for-station";
import { StopFaciliteitenDialog } from "./stop-faciliteiten-dialog";

interface BestaandeAanvraagRegievoerderProps {
    vestigingen: Vestiging[];
    setVariant: React.Dispatch<React.SetStateAction<"nieuweAanvraagRegievoerder" | "bestaandeAanvraagRegievoerder">>;
    onVestigingenUpdate: VestigingenUpdateHandler;
}

export function BestaandeAanvraagRegievoerder({
    vestigingen,
    setVariant,
    onVestigingenUpdate,
}: Readonly<BestaandeAanvraagRegievoerderProps>) {
    const [stopFaciliteitenDialogOpen, setStopFaciliteitenDialogOpen] = useState(false);

    const vestigingenByStation = useMemo(() => Vestiging.groupByStation(vestigingen), [vestigingen]);

    const handleStopRequest = useCallback(
        async (vestigingen: Vestiging[]) => {
            await onVestigingenUpdate(
                vestigingen.reduce((acc: Record<number, AangevraagdeVestiging>, vestiging) => {
                    if (vestiging.aangevraagdeVestiging) {
                        acc[vestiging.aangevraagdeVestiging.id] = AangevraagdeVestiging.update(vestiging.aangevraagdeVestiging, {
                            status: "StopAangevraagd",
                        });
                    }
                    return acc;
                }, {})
            );
            setStopFaciliteitenDialogOpen(false);
        },
        [onVestigingenUpdate]
    );

    const handleUpdateOpeningstijd = useCallback(
        async (vestiging: Vestiging, updatedOpeningstijden: AangevraagdeVestigingOpeningstijd[]) => {
            if (vestiging.aangevraagdeVestiging) {
                await onVestigingenUpdate({
                    [vestiging.aangevraagdeVestiging.id]: AangevraagdeVestiging.update(vestiging.aangevraagdeVestiging, {
                        openingstijden: updatedOpeningstijden,
                    }),
                });
            }
        },
        [onVestigingenUpdate]
    );

    const handlers = useMemo(
        () => ({
            onStopRequest: handleStopRequest,
            onUpdateOpeningstijd: handleUpdateOpeningstijd,
        }),
        [handleStopRequest, handleUpdateOpeningstijd]
    );

    return (
        <DossierDetailsPanel>
            <DossierDetailsHeader section="Faciliteiten" showBackButton showBerichtBalk>
                <div className="flex items-center justify-end gap-2">
                    <Button
                        icon="station"
                        iconStrokeWidth="thick"
                        variant="secondary"
                        onClick={() => setVariant("nieuweAanvraagRegievoerder")}
                    >
                        Nieuwe aanvraag
                    </Button>
                    {Vestiging.canRequestBulkStop(Object.values(vestigingenByStation).flat()) && (
                        <ActionsDropdown data-testid="stop-alle-vestigingen">
                            <DropdownMenuItem className="min-w-80" onClick={() => setStopFaciliteitenDialogOpen(true)}>
                                Verzoek stoppen alle actieve faciliteiten
                            </DropdownMenuItem>
                        </ActionsDropdown>
                    )}
                </div>
            </DossierDetailsHeader>
            <DossierDetailsContent className="space-y-6">
                <div className="space-y-6">
                    {Object.entries(vestigingenByStation).map(([stationCode, vestigingen]) => (
                        <FaciliteitenDetailsForStation
                            key={stationCode}
                            stationCode={stationCode}
                            vestigingen={vestigingen}
                            handlers={handlers}
                        />
                    ))}
                </div>

                <StopFaciliteitenDialog
                    open={stopFaciliteitenDialogOpen}
                    onOpenChange={setStopFaciliteitenDialogOpen}
                    variant={{ type: "all" }}
                    onConfirm={() => {
                        void handleStopRequest(
                            Object.values(vestigingenByStation)
                                .flat()
                                .filter((v) => Vestiging.canRequestStop(v))
                        );
                    }}
                    onCancel={() => setStopFaciliteitenDialogOpen(false)}
                />
            </DossierDetailsContent>
        </DossierDetailsPanel>
    );
}
